"use client";

import { useEffect, useLayoutEffect } from "react";
import { usePathname } from "next/navigation";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

/**
 * Page motion, browser only: the hero copy rises in on each page, and every
 * [data-reveal] element fades up once as it scrolls into view. The server
 * HTML stays fully visible for crawlers and for visitors without JavaScript.
 *
 * Nothing moves for visitors who ask for reduced motion. If the browser is
 * not drawing frames (a hidden tab, a paused preview), everything is shown as
 * it is instead of waiting invisible for an animation that never runs.
 */
export function PageMotion() {
  const pathname = usePathname();

  // The glass bars grow in with a CSS animation, but only once this class is
  // on <html>. Without it they are simply shown, so a hidden tab, a crawler or
  // a screenshot tool never sees an empty band waiting on an animation that a
  // browser has paused. The class goes on when the page is actually visible.
  useEffect(() => {
    const root = document.documentElement;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const on = () => {
      if (document.visibilityState !== "visible") return;
      document.removeEventListener("visibilitychange", on);
      // Two painted frames prove the browser is really drawing; a paused
      // preview or background renderer never gets here, and the bars stay shown.
      requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add("motion-ready")));
    };
    on();
    document.addEventListener("visibilitychange", on);
    return () => document.removeEventListener("visibilitychange", on);
  }, []);

  useLayoutEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    gsap.registerPlugin(ScrollTrigger);
    const startFrame = gsap.ticker.frame;
    let fallback = 0;
    const ctx = gsap.context(() => {
      const heroBits = gsap.utils.toArray<HTMLElement>(".phero-copy > *, .phero-facts li");
      if (heroBits.length) {
        gsap.from(heroBits, { y: 26, autoAlpha: 0, duration: 1, ease: "power3.out", stagger: 0.07, delay: 0.05 });
        gsap.from(".phero-media .photo", { scale: 1.06, duration: 1.8, ease: "power2.out" });
      }
      const items = gsap.utils.toArray<HTMLElement>("[data-reveal]");
      gsap.set(items, { y: 28, autoAlpha: 0 });
      ScrollTrigger.batch(items, {
        start: "top 90%",
        once: true,
        onEnter: (batch) =>
          gsap.to(batch, { y: 0, autoAlpha: 1, duration: 0.9, ease: "power3.out", stagger: 0.08, overwrite: true }),
      });
      ScrollTrigger.refresh();
      fallback = window.setTimeout(() => {
        if (gsap.ticker.frame - startFrame < 10) {
          ScrollTrigger.getAll().forEach((t) => t.kill());
          gsap.killTweensOf([...heroBits, ...items]);
          gsap.set([...heroBits, ...items, ".phero-media .photo"], { clearProps: "opacity,visibility,transform" });
        }
      }, 1800);
    });
    return () => {
      window.clearTimeout(fallback);
      ctx.revert();
    };
  }, [pathname]);

  return null;
}
